import { useState } from 'react'

import { Alert, Box, Icon, Text } from '@factorialco/deprecated-design-system'
import { DocumentsDocument } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'
import { employeeUser } from 'modules/core/store/collections/users'
import type User from 'modules/core/store/models/User'

import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import { Document } from 'modules/documents/hooks/documents/useGetDocuments'

import { TaskDetail } from '../../TaskElement/useTaskDetail'
import DocumentFile from './DocumentFile'

interface Props {
  task: TaskDetail
  owner: User | undefined
  contractVersions: ContractVersions
}

const DocumentSignatures = ({ task, owner, contractVersions }: Props) => {
  // eslint-disable-next-line no-type-assertion/no-type-assertion
  const taskDetail = task as TaskDetail
  const [documents, setDocuments] = useState<Document[] | undefined>(undefined)

  const documentIds = (
    taskDetail.signatureDocumentsTypeData?.documentIds || [
      taskDetail.sendDocumentTemplateTypeData?.documentId,
    ]
  ).filter((id): id is number => id !== undefined)

  const { data: documentsData, loading } = useQuery(DocumentsDocument, {
    variables: {
      ids: documentIds,
      sort: 'created_at-desc',
    },
  })

  useUnsafeEffect(() => {
    setDocuments(documentsData?.documents.documents)
  }, [documentsData])

  const affectee = task.affectees[0]

  const employeeName =
    affectee?.id !== owner?.employee?.attributes.access_id
      ? affectee?.fullName
      : owner?.employee && employeeUser(owner?.employee)?.fullName()

  if (loading) return null

  return (
    <Box gap="s16">
      <Text.Header2 color="grey800">{i18n.t('tasks.types.sign_document.title')}</Text.Header2>
      <Box gap="s16">
        {documents?.length ? (
          documents?.map((document) => (
            <DocumentFile
              document={document}
              key={document.id}
              documents={documents}
              contractVersions={contractVersions}
            />
          ))
        ) : (
          <Alert color="warning">
            <Box flexDirection="row" gap="s8">
              <Box flexNoShrink>
                <Icon.Medium icon="alert-circle" color="grey800" />
              </Box>

              <Box>
                <Text.Normal weight="medium">
                  {i18n.t('tasks.not_permission.first', {
                    employee_name: employeeName,
                  })}
                </Text.Normal>

                <Text.Normal weight="medium">{i18n.t('tasks.not_permission.second')}</Text.Normal>
              </Box>
            </Box>
          </Alert>
        )}
      </Box>
    </Box>
  )
}

export default observer(DocumentSignatures)
